import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Card, Chip, useTheme } from 'react-native-paper';

interface EquipmentPreviewProps {
  data: any;
}

export function EquipmentPreview({ data }: EquipmentPreviewProps) {
  const theme = useTheme();

  const formatPrice = (
    min?: number,
    max?: number,
    currency: string = 'USD'
  ) => {
    if (!min && !max) return null;
    const symbol = currency === 'USD' ? '$' : currency;
    if (min && max) {
      return `${symbol}${min} - ${symbol}${max}`;
    }
    return min ? `${symbol}${min}+` : `Up to ${symbol}${max}`;
  };

  const formatDimensions = (dimensions?: any) => {
    if (
      !dimensions ||
      (!dimensions.width && !dimensions.height && !dimensions.depth)
    ) {
      return null;
    }
    const { width, height, depth, unit = 'cm' } = dimensions;
    const parts = [];
    if (width) parts.push(`W: ${width}${unit}`);
    if (height) parts.push(`H: ${height}${unit}`);
    if (depth) parts.push(`D: ${depth}${unit}`);
    return parts.join(' × ');
  };

  const formatWeight = (weight?: number, unit: string = 'g') => {
    if (!weight) return null;
    return `${weight}${unit}`;
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineMedium' style={styles.title}>
            {data.name || 'Equipment Name'}
          </Text>

          <Text variant='titleSmall' style={styles.brand}>
            {data.brand || 'Brand'}
            {data.model ? ` - ${data.model}` : ''}
          </Text>

          <Text
            variant='bodyMedium'
            style={[
              styles.description,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {data.description || 'Equipment description will appear here...'}
          </Text>

          <View style={styles.metadata}>
            <Chip mode='outlined' compact>
              {data.equipment_type?.replace(/_/g, ' ') || 'Other'}
            </Chip>
            <Chip mode='outlined' compact>
              {data.price_range?.replace(/_/g, ' ') || 'Mid Range'}
            </Chip>
          </View>
        </Card.Content>
      </Card>

      {/* Main Image */}
      {(data.featured_image_url ||
        (data.gallery_images && data.gallery_images.length > 0)) && (
        <Card style={styles.card}>
          <Card.Cover
            source={{ uri: data.featured_image_url || data.gallery_images[0] }}
            style={styles.mainImage}
          />
        </Card>
      )}

      {/* Pricing */}
      {(data.price_min || data.price_max) && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Pricing
            </Text>

            <View style={styles.pricingInfo}>
              <Text variant='headlineSmall' style={styles.priceText}>
                {formatPrice(data.price_min, data.price_max, data.currency)}
              </Text>
              <Text
                variant='bodySmall'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {data.price_range?.replace(/_/g, ' ') || 'Mid Range'}
              </Text>
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Specifications */}
      {data.specifications && Object.keys(data.specifications).length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Specifications
            </Text>

            <View style={styles.specGrid}>
              {Object.entries(data.specifications).map(
                ([key, value]: [string, any]) => (
                  <View key={key} style={styles.specItem}>
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.specLabel,
                        { color: theme.colors.onSurfaceVariant },
                      ]}
                    >
                      {key.replace(/_/g, ' ')}
                    </Text>
                    <Text variant='bodyMedium'>
                      {typeof value === 'object'
                        ? JSON.stringify(value)
                        : String(value)}
                    </Text>
                  </View>
                )
              )}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Dimensions & Weight */}
      {(formatDimensions(data.dimensions) ||
        formatWeight(data.weight, data.weight_unit)) && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Physical Information
            </Text>

            <View style={styles.specGrid}>
              {formatDimensions(data.dimensions) && (
                <View style={styles.specItem}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.specLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Dimensions
                  </Text>
                  <Text variant='bodyMedium'>
                    {formatDimensions(data.dimensions)}
                  </Text>
                </View>
              )}

              {formatWeight(data.weight, data.weight_unit) && (
                <View style={styles.specItem}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.specLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Weight
                  </Text>
                  <Text variant='bodyMedium'>
                    {formatWeight(data.weight, data.weight_unit)}
                  </Text>
                </View>
              )}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Materials */}
      {data.materials && data.materials.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Materials
            </Text>

            <View style={styles.tagsContainer}>
              {data.materials.map((material: string, index: number) => (
                <Chip key={index} mode='outlined' compact style={styles.tag}>
                  {material}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Features */}
      {data.features && data.features.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Features
            </Text>

            {data.features.map((feature: string, index: number) => (
              <View key={index} style={styles.featureItem}>
                <Text variant='bodyMedium'>• {feature}</Text>
              </View>
            ))}
          </Card.Content>
        </Card>
      )}

      {/* Affiliate Links */}
      {data.affiliate_links && Object.keys(data.affiliate_links).length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Where to Buy
            </Text>

            {Object.entries(data.affiliate_links).map(
              ([retailer, url]: [string, any]) => (
                <View key={retailer} style={styles.linkItem}>
                  <Text variant='titleMedium' style={styles.retailerName}>
                    {retailer}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[styles.linkUrl, { color: theme.colors.primary }]}
                    numberOfLines={1}
                  >
                    {url}
                  </Text>
                </View>
              )
            )}
          </Card.Content>
        </Card>
      )}

      {/* Color Options */}
      {data.color_options && data.color_options.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Available Colors
            </Text>

            <View style={styles.tagsContainer}>
              {data.color_options.map((color: string, index: number) => (
                <Chip key={index} mode='outlined' compact style={styles.tag}>
                  {color}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Tags */}
      {data.tags && data.tags.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Tags
            </Text>

            <View style={styles.tagsContainer}>
              {data.tags.map((tag: string, index: number) => (
                <Chip key={index} mode='outlined' compact style={styles.tag}>
                  {tag}
                </Chip>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* Video */}
      {data.video_url && (
        <Card style={styles.card}>
          <Card.Content>
            <Text variant='titleLarge' style={styles.sectionTitle}>
              Video
            </Text>

            <View style={styles.videoPlaceholder}>
              <Text
                variant='bodyMedium'
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                🎥 Video will be embedded here
              </Text>
              <Text
                variant='bodySmall'
                style={[styles.videoUrl, { color: theme.colors.primary }]}
              >
                {data.video_url}
              </Text>
            </View>
          </Card.Content>
        </Card>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  card: {
    marginBottom: 16,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  brand: {
    marginBottom: 8,
    fontWeight: '600',
  },
  description: {
    marginBottom: 16,
    lineHeight: 20,
  },
  metadata: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  mainImage: {
    height: 200,
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 16,
  },
  pricingInfo: {
    alignItems: 'flex-start',
  },
  priceText: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  specGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  specItem: {
    flex: 1,
    minWidth: '45%',
  },
  specLabel: {
    marginBottom: 4,
    textTransform: 'capitalize',
  },
  featureItem: {
    marginBottom: 8,
  },
  linkItem: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  retailerName: {
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'capitalize',
  },
  linkUrl: {
    fontSize: 12,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    marginBottom: 4,
  },
  videoPlaceholder: {
    alignItems: 'center',
    paddingVertical: 32,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
  },
  videoUrl: {
    marginTop: 8,
    textAlign: 'center',
  },
});
